import {
  KeyboardEvent,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { CellProps, Row, useSortBy, useTable } from "react-table";
import { ethers } from "ethers";
import { ReactComponent as Copy } from "../../assets/svg/copy.svg";
import { DataContext } from "../../context/DataContext";
import { PageEndpointEnum } from "../../enums/endpoints";
import {
  IMegaRewardsAccount,
  useMegaRewards,
} from "../../hooks/api/staking/useMegaRewards";
import { ToastEnum, ToastStatusEnum, useToast } from "../../hooks/toast";
import { DefaultCellForColumn } from "../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../Table/DefaultHeaderForColumn";
import { ITableColumn } from "../Table/TableColumn";
import {
  Align,
  CopyButton,
  DashboardRow,
  TableHeaderCell,
  TitleWrapper,
} from "../Table/style";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { Spinner } from "../shared/Spinner";
import {
  AccountCell,
  AccountLabel,
  MegaHint,
  MegaNotice,
  MegaSearch,
  MegaSummary,
  MegaTableWrapper,
  MegaToolbar,
  RewardCell,
  StatusCell,
} from "./MegaRewards.styles";
import { getPerpRewardsSummary } from "./megaRewardsSummary";
import { Tab, TabsWrapper } from "./style";

const numericValue = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const formatNumber = (
  value: string | number | null | undefined,
  decimals = 2
) => {
  const number = numericValue(value);
  return number === undefined
    ? "—"
    : number.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
};

const formatUSD = (value: string | number | null | undefined) =>
  numericValue(value) === undefined ? "—" : `$${formatNumber(value)}`;

const checksumAddress = (address: string) => {
  try {
    return ethers.utils.getAddress(address);
  } catch {
    return address;
  }
};

const rejectionLabels: Record<string, string> = {
  duration_tier_not_commander_or_legend: "Requires Commander or Legend",
  zero_current_stake: "No active stake",
  missing_ratchet_amount: "Missing qualifying stake history",
  below_required_staked_amount: "Below required stake",
  volume_query_error: "Volume unavailable",
  below_volume_threshold: "Below $10M qualifying volume",
};

const accountStatus = (account: IMegaRewardsAccount) => {
  if (account.error) return "Volume unavailable";
  if (account.reject_reasons?.length) {
    const reason = account.reject_reasons[0];
    return rejectionLabels[reason] || reason.replace(/_/g, " ");
  }
  if (account.ranking) {
    return account.is_snapshot_eligible ? "Eligible" : "Pending snapshot";
  }
  return "Awaiting reward estimate";
};

const numericSort = (
  first: Row<IMegaRewardsAccount>,
  second: Row<IMegaRewardsAccount>,
  columnId: string
) =>
  (numericValue(first.values[columnId]) ?? -1) -
  (numericValue(second.values[columnId]) ?? -1);

export function MegaRewards() {
  const { data, error, isLoading, isValidating, mutate } = useMegaRewards();
  const { records, isAirtableTagsLoading } = useContext(DataContext);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"all" | "perp">("all");
  const allTabRef = useRef<HTMLButtonElement>(null);
  const perpTabRef = useRef<HTMLButtonElement>(null);
  const onViewKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    let next: "all" | "perp" = "all";
    if (event.key === "End") next = "perp";
    else if (event.key !== "Home") next = view === "all" ? "perp" : "all";
    setView(next);
    (next === "all" ? allTabRef : perpTabRef).current?.focus();
  };
  const hasVolumeErrors = (data?.summary.volume_query_errors || 0) > 0;
  const refresh = () => {
    mutate().catch(() => {
      // The hook exposes the retry failure in the error state below.
    });
  };

  const getTag = useCallback(
    (account: string) =>
      records[checksumAddress(account)] ||
      records[account.toLowerCase()] ||
      records[account],
    [records]
  );

  const copyAddress = useCallback(
    async (account: string) => {
      try {
        await navigator.clipboard.writeText(account);
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Address Copied",
            status: ToastStatusEnum.SUCCESS,
          },
          4000
        );
      } catch {
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Unable to copy address",
            status: ToastStatusEnum.ERROR,
          },
          4000
        );
      }
    },
    [addToast]
  );

  const accounts = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (data?.rows || []).filter(
      (account) =>
        !query ||
        [account.account, account.username, getTag(account.account)].some(
          (value) => value?.toLowerCase().includes(query)
        )
    );
  }, [data?.rows, search, getTag]);

  const perpSummary = useMemo(
    () => getPerpRewardsSummary(data?.rows || [], getTag, hasVolumeErrors),
    [data?.rows, getTag, hasVolumeErrors]
  );
  const perpTagsLoading =
    isAirtableTagsLoading && perpSummary.accounts.length === 0;

  const renderAccount = useCallback(
    (account: IMegaRewardsAccount) => {
      const address = checksumAddress(account.account);
      const tag = getTag(account.account);
      return (
        <AccountCell>
          <CopyButton
            type="button"
            aria-label={`Copy ${address}`}
            onClick={(event) => {
              event.stopPropagation();
              copyAddress(address);
            }}
          >
            <Copy />
          </CopyButton>
          <AccountLabel
            href={`${PageEndpointEnum.ACCOUNT}/${address}`}
            title={[tag, account.username, address].filter(Boolean).join("\n")}
            onClick={(event) => {
              event.stopPropagation();
              if (
                !event.metaKey &&
                !event.ctrlKey &&
                !event.shiftKey &&
                !event.altKey
              ) {
                event.preventDefault();
                navigate(`${PageEndpointEnum.ACCOUNT}/${address}`);
              }
            }}
          >
            {tag || account.username || address}
          </AccountLabel>
        </AccountCell>
      );
    },
    [copyAddress, getTag, navigate]
  );

  const columns = useMemo<ITableColumn<IMegaRewardsAccount>[]>(
    () => [
      {
        title: "Rank",
        accessor: "ranking",
        width: "5%",
        Cell: ({ value }) => <span>{value || "—"}</span>,
      },
      {
        title: "Account",
        accessor: "account",
        width: "27%",
        Cell: ({ row }) => renderAccount(row.original),
      },
      {
        title: "Qualifying Volume",
        accessor: "total_volume",
        align: "right",
        width: "15%",
        sortType: numericSort,
        sortDescFirst: true,
        Cell: ({ value, row }) => (
          <Align align="right">
            {row.original.error ? "—" : formatUSD(value)}
          </Align>
        ),
      },
      {
        title: "Staking Tier",
        accessor: "staking_tier",
        width: "12%",
        Cell: ({ value }) => (
          <span style={{ whiteSpace: "normal" }}>{value || "—"}</span>
        ),
      },
      {
        title: "Staked AEVO",
        accessor: "current_staked_amount",
        align: "right",
        width: "13%",
        sortType: numericSort,
        sortDescFirst: true,
        Cell: ({ value }) => <Align align="right">{formatNumber(value)}</Align>,
      },
      {
        title: "Expected Reward",
        accessor: "expected_reward_pool_percentage",
        align: "right",
        width: "15%",
        sortType: numericSort,
        sortDescFirst: true,
        Cell: ({ value, row }) => {
          const account = row.original;
          const percentage = hasVolumeErrors ? undefined : numericValue(value);
          return (
            <RewardCell>
              {hasVolumeErrors ? "—" : formatUSD(account.expected_reward_usdc)}
              {percentage !== undefined && (
                <small>
                  {formatNumber(percentage)}%
                  {account.is_capped ? " · Capped" : ""}
                </small>
              )}
            </RewardCell>
          );
        },
      },
      {
        id: "status",
        title: "Status",
        accessor: accountStatus,
        width: "13%",
        Cell: ({ value, row }: CellProps<IMegaRewardsAccount, string>) => (
          <StatusCell
            title={
              row.original.ranking && !row.original.is_snapshot_eligible
                ? `Requires a qualifying stake through epoch ${row.original.required_snapshot_epoch}`
                : value
            }
          >
            {value}
          </StatusCell>
        ),
      },
    ],
    [renderAccount, hasVolumeErrors]
  );

  const table = useTable<IMegaRewardsAccount>(
    {
      columns,
      data: view === "perp" ? perpSummary.accounts : accounts,
      defaultColumn: {
        Header: DefaultHeaderForColumn,
        Cell: DefaultCellForColumn,
      } as any,
      initialState: {
        sortBy: [{ id: "expected_reward_pool_percentage", desc: true }],
      },
      autoResetSortBy: false,
      getRowId: (account) => account.account,
    },
    useSortBy
  );

  let emptyMessage = search.trim()
    ? "No accounts match your search."
    : "No active staking accounts.";
  if (view === "perp")
    emptyMessage = perpTagsLoading
      ? "Loading account tags…"
      : "No Aevo Rewards Perp accounts.";

  return (
    <>
      <TitleWrapper>
        <h3>Mega Rewards</h3>
      </TitleWrapper>
      <TabsWrapper role="tablist" aria-label="Mega Rewards views">
        <Tab
          ref={allTabRef}
          id="mega-rewards-all-tab"
          type="button"
          role="tab"
          isActive={view === "all"}
          aria-selected={view === "all"}
          aria-controls="mega-rewards-table-panel"
          tabIndex={view === "all" ? 0 : -1}
          onClick={() => setView("all")}
          onKeyDown={onViewKeyDown}
        >
          All Accounts
        </Tab>
        <Tab
          ref={perpTabRef}
          id="mega-rewards-perp-tab"
          type="button"
          role="tab"
          isActive={view === "perp"}
          aria-selected={view === "perp"}
          aria-controls="mega-rewards-table-panel"
          tabIndex={view === "perp" ? 0 : -1}
          onClick={() => setView("perp")}
          onKeyDown={onViewKeyDown}
        >
          Aevo Rewards Perp
        </Tab>
      </TabsWrapper>
      <MegaToolbar>
        <MegaSummary>
          <span>Accounts: {formatNumber(data?.summary.total_stakers, 0)}</span>
          <span>
            Eligible:{" "}
            {formatNumber(data?.summary.snapshot_eligible_candidates, 0)}
          </span>
          <span>Reward pool: {formatUSD(data?.pool_usdc)} USDC</span>
        </MegaSummary>
        {view === "all" && (
          <MegaSearch
            aria-label="Search Mega Rewards accounts"
            placeholder="Search account, username or tag"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        )}
      </MegaToolbar>
      <MegaHint>
        Finalized qualifying volume
        {data?.volume_window_start_epoch && data?.volume_window_end_epoch
          ? ` · Epochs ${data.volume_window_start_epoch}–${data.volume_window_end_epoch}`
          : ""}
      </MegaHint>
      {error && (
        <MegaNotice role="alert">
          {data
            ? "Unable to refresh Mega Rewards. Showing last loaded values."
            : "Unable to load Mega Rewards."}
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            disabled={isValidating}
            onClick={refresh}
          >
            Retry
          </Button>
        </MegaNotice>
      )}
      {hasVolumeErrors && (
        <MegaNotice role="alert">
          Some qualifying volumes are unavailable. Reward estimates are withheld
          until all volumes load.
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            disabled={isValidating}
            onClick={refresh}
          >
            Retry
          </Button>
        </MegaNotice>
      )}
      {isLoading && !data && (
        <div role="status" aria-label="Loading Mega Rewards">
          <Spinner />
        </div>
      )}
      {data && (
        <div
          role="tabpanel"
          id="mega-rewards-table-panel"
          aria-labelledby={
            view === "all" ? "mega-rewards-all-tab" : "mega-rewards-perp-tab"
          }
        >
          <MegaTableWrapper>
            <table
              {...table.getTableProps()}
              aria-label={
                view === "perp"
                  ? "Aevo Rewards Perp accounts"
                  : "Mega Rewards accounts"
              }
            >
              <thead>
                {table.headerGroups.map((group) => {
                  const { key, ...props } = group.getHeaderGroupProps();
                  return (
                    <tr key={key} {...props}>
                      {group.headers.map((column) => {
                        const { key: columnKey, ...columnProps } =
                          column.getHeaderProps(column.getSortByToggleProps());
                        return (
                          <TableHeaderCell
                            key={columnKey}
                            {...columnProps}
                            style={{ width: column.width }}
                          >
                            {column.render("Header")}
                          </TableHeaderCell>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>
              <tbody {...table.getTableBodyProps()}>
                {table.rows.map((row) => {
                  table.prepareRow(row);
                  const { key, ...props } = row.getRowProps();
                  return (
                    <DashboardRow
                      key={key}
                      {...props}
                      hasRecord={!!getTag(row.original.account)}
                    >
                      {row.cells.map((cell) => {
                        const { key: cellKey, ...cellProps } =
                          cell.getCellProps();
                        return (
                          <td key={cellKey} {...cellProps}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </DashboardRow>
                  );
                })}
                {table.rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{ textAlign: "center" }}
                    >
                      {emptyMessage}
                    </td>
                  </tr>
                )}
              </tbody>
              {view === "perp" && (
                <tfoot>
                  <tr>
                    <th scope="row" colSpan={5}>
                      Total
                      {!perpTagsLoading &&
                        ` (${perpSummary.accounts.length} accounts)`}
                    </th>
                    <td>
                      <RewardCell>
                        {perpTagsLoading
                          ? "—"
                          : formatUSD(perpSummary.totalReward)}
                        <small>
                          {perpTagsLoading ||
                          perpSummary.totalPercentage === undefined
                            ? "—"
                            : `${formatNumber(perpSummary.totalPercentage)}%`}
                        </small>
                      </RewardCell>
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </MegaTableWrapper>
        </div>
      )}
    </>
  );
}
